import { Snackbar as MuiSnackbar, SnackbarCloseReason, SnackbarContent } from '@mui/material';
import { useEffect, useState } from 'react';

import { theme } from '@/modules/common/theme';
import { AUTO_HIDE_DURATION } from '../constants';
import { useSnackbarStore } from '../store/useSnackbarStore';

type Props = {
  justifyContent: 'flex-start' | 'center' | 'flex-end';
  rightOffset: string;
  bottomOffset: string;
};

type SnackbarState = {
  activeSnackbar: string | null;
  snackPack: readonly string[];
  open: boolean;
  key: number;
};

function queueSnackbar(state: SnackbarState, message: string): SnackbarState {
  if (state.activeSnackbar === null) {
    // Set a new snack when we don't have an active one
    return {
      ...state,
      activeSnackbar: message,
      open: true,
      key: state.key + 1,
    };
  }

  // Close an active snack when a new one is added
  return {
    ...state,
    snackPack: [...state.snackPack, message],
    open: false,
  };
}

export function Snackbar({ justifyContent, rightOffset, bottomOffset }: Props) {
  const [snackbarState, setSnackbarState] = useState<SnackbarState>(() => {
    const { message } = useSnackbarStore.getState();

    return {
      activeSnackbar: message,
      snackPack: [],
      open: message !== null,
      key: 0,
    };
  });

  useEffect(() => {
    const unsubscribe = useSnackbarStore.subscribe((state, previousState) => {
      if (state.message === null || state.message === previousState.message) return;

      setSnackbarState((previous) => queueSnackbar(previous, state.message));
      state.resetMessage();
    });

    const { message, resetMessage } = useSnackbarStore.getState();
    if (message !== null) resetMessage();

    return unsubscribe;
  }, []);

  const handleClose = (_event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'escapeKeyDown') return;

    setSnackbarState((previous) => (previous.open ? { ...previous, open: false } : previous));
  };

  const handleExited = () => {
    setSnackbarState((previous) => {
      if (previous.open || previous.activeSnackbar === null) return previous;

      const [nextSnackbar, ...remainingSnackPack] = previous.snackPack;

      if (nextSnackbar === undefined) {
        return { ...previous, activeSnackbar: null };
      }

      return {
        activeSnackbar: nextSnackbar,
        snackPack: remainingSnackPack,
        open: true,
        key: previous.key + 1,
      };
    });
  };

  const { activeSnackbar, open, key } = snackbarState;

  return (
    <MuiSnackbar
      key={key}
      open={open}
      autoHideDuration={AUTO_HIDE_DURATION}
      onClose={handleClose}
      TransitionProps={{ onExited: handleExited }}
      ClickAwayListenerProps={{ onClickAway: () => null }}
      sx={{
        '&:hover': { cursor: 'default' },
        justifyContent,
        '&.MuiSnackbar-root': {
          position: 'fixed',
          bottom: bottomOffset,
          right: rightOffset,
        },
      }}
    >
      <SnackbarContent
        style={{
          display: 'block',
          textAlign: 'left',
          minWidth: 0,
          maxWidth: '500px',
          borderRadius: '4px',
          backgroundColor: theme.palette.neutral.darker,
        }}
        message={activeSnackbar}
      />
    </MuiSnackbar>
  );
}
